import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  styled, 
  Modal, 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  InputLabel, 
  FormControl, 
  Chip, 
  OutlinedInput, 
  Autocomplete, 
  Checkbox, 
  ListItemText, 
  Divider,
  Breadcrumbs,
  Link,
  IconButton,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CloseIcon from '@mui/icons-material/Close';

// Import images
import GrantAccessIcon from '../assets/images/light/Grant-access.png';
import RevokeAccessIcon from '../assets/images/light/Revoke-access.png';
import DropdownIconImg from '../assets/icons/light/dropdown-icon.png';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 3, 3, 3),
  backgroundColor: theme.palette.background.default,
  minHeight: 'calc(100vh - 64px)', // Assuming header height is 64px
}));

const BreadcrumbContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1000px',
  margin: '-10px auto 15px auto',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  position: 'relative',
  top: '-8px'
}));

const BackButton = styled(Button)(({ theme }) => ({
  color: 'var(--B5, #4299D4)',
  textTransform: 'none',
  fontFamily: 'Proxima Nova',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  display: 'flex',
  alignItems: 'center',
  padding: '4px 8px',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'none',
  },
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1000px',
  margin: '0 auto',
  paddingLeft: theme.spacing(1), // Reduced from 4 to 2 (32px to 16px)
  marginBottom: theme.spacing(4),
}));

const PageTitle = styled(Typography)({
  color: '#1674B4',
  fontFamily: 'Proxima Nova',
  fontSize: '30px',
  fontWeight: 500,
  lineHeight: 'normal',
  textAlign: 'left',
});

const CardsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1000px',
  margin: '0 auto',
}));

const CardStyled = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'left',
  color: theme.palette.text.secondary,
  width: '470px',
  height: '124px',
  flexShrink: 0,
  borderRadius: '8px',
  backgroundColor: '#FFF',
  boxShadow: '2px 2px 2px 0px rgba(0, 0, 0, 0.25)',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  cursor: 'pointer',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '2px 4px 4px 0px rgba(0, 0, 0, 0.3)',
  },
  margin: theme.spacing(1),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  marginLeft: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '80px',
  '& img': {
    width: '75px',
    height: '75px',
    objectFit: 'contain',
  },
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  color: '#1674B4',
  fontFamily: 'Proxima Nova',
  fontSize: '20px',
  fontWeight: 600,
  lineHeight: 'normal',
  textAlign: 'left',
  width: '100%',
}));

const CardDescription = styled(Typography)({
  color: '#6D6D6D',
  fontFamily: 'Proxima Nova',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 'normal',
  textAlign: 'left',
  width: '100%',
});

const PageDescription = styled(Typography)({
  color: 'var(--BK9, #2E2E2E)',
  fontFamily: 'Proxima Nova',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 'normal',
  textAlign: 'left',
  width: '100%',
  marginBottom: '24px',
  '& strong': {
    fontWeight: 600,
  },
});

const ModalStyle = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '600px',
  padding: '24px',
  borderRadius: '8px',
  backgroundColor: '#FFF',
  boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.15)',
  outline: 'none',
  '& .MuiAutocomplete-root': {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      width: '550px',
      height: '50px',
      padding: '6px 12px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0,
      borderRadius: '4px',
      border: '1px solid var(--BK3, #D9D9D9)',
      backgroundColor: 'var(--BK1, #FFF)',
      boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.25)',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
    },
  },
  '& .MuiFormControl-root': {
    marginBottom: '5px',
    '& .MuiOutlinedInput-root': {
      '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '8px',
        height: '24px',
      },
      '&:hover': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#1674B4',
        },
      },
      width: '550px',
      height: '50px',
      padding: '6px 12px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0,
      borderRadius: '4px',
      border: '1px solid var(--BK3, #D9D9D9)',
      backgroundColor: 'var(--BK1, #FFF)',
      boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.25)',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
    },
  },
  '& .MuiButton-root': {
    textTransform: 'none',
    padding: '8px 20px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
  },
  '& .MuiButton-outlined': {
    borderColor: '#D1D1D1',
    color: '#2E2E2E',
    marginRight: '12px',
  },
  '& .MuiButton-contained': {
    backgroundColor: '#1674B4',
    '&:hover': {
      backgroundColor: '#126499',
    },
  },
}));

const ModalTitle = styled(Typography)({
  color: 'var(--B6, #1674B4)',
  fontFamily: 'Proxima Nova',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: 'normal',
  marginBottom: '24px',
});

function UserConfigPage() {
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // For user emails/IDs
  const [selectedSections, setSelectedSections] = useState([]);
  const [modalType, setModalType] = useState(''); // 'grant' or 'revoke'
  const [openModal, setOpenModal] = useState(false);
  const [userSections, setUserSections] = useState({});
  const [isLoadingSections, setIsLoadingSections] = useState(false);
  const [applicationSections, setApplicationSections] = useState([]);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  // Custom dropdown icon component
  const DropdownIcon = (props) => (
    <img 
      src={DropdownIconImg} 
      alt="dropdown" 
      style={{ width: '24px', height: '24px' }}
      {...props}
    />
  );

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  // Fetch all users and sections on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';
        
        // Fetch users
        const usersResponse = await axios.get(`${API_URL}/api/user-access/users`);
        setAllUsers(usersResponse.data);
        
        // Fetch all available sections
        const sectionsResponse = await axios.get(`${API_URL}/api/user-access/sections`);
        setApplicationSections(sectionsResponse.data);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        toast.error('Failed to load initial data');
      }
    };

    fetchInitialData();
  }, []);

  // Fetch sections for selected user
  useEffect(() => {
    const fetchSelectedUsersSections = async () => {
      if (selectedUsers.length === 1) {
        const userId = selectedUsers[0].id;
        if (!userSections[userId]) {
          setIsLoadingSections(true);
          try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';
            const response = await axios.get(`${API_URL}/api/user-access/users/${userId}/sections`);
            setUserSections(prev => ({
              ...prev,
              [userId]: response.data
            }));
          } catch (error) {
            console.error('Error fetching user sections:', error);
            toast.error('Failed to load user sections');
          } finally {
            setIsLoadingSections(false);
          }
        }
      }
    };

    fetchSelectedUsersSections();
  }, [selectedUsers, userSections]);

  // Reset selected sections when users change
  useEffect(() => {
    setSelectedSections([]);
  }, [selectedUsers]);

  // Get filtered sections based on modal type
  const getFilteredSections = () => {
    if (selectedUsers.length !== 1) {
      return applicationSections;
    }

    const userId = selectedUsers[0].id;
    const userCurrentSections = userSections[userId] || [];

    if (modalType === 'grant') {
      // For grant dialog, show only sections the user doesn't have
      return applicationSections.filter(
        section => !userCurrentSections.includes(section.id)
      );
    } else {
      // For revoke dialog, show only sections the user has
      return applicationSections.filter(
        section => userCurrentSections.includes(section.id)
      );
    }
  };

  const handleOpenModal = (type) => {
    setModalType(type);
    setOpenModal(true);
  };

  const handleCloseModal = (event, reason) => {
    // Only close if explicitly called (not from backdrop click)
    if (reason === 'backdropClick') {
      return;
    }
    setOpenModal(false);
    setSelectedUsers([]);
    setSelectedSections([]);
  };

  const handleUserChange = (event, newValue) => {
    setSelectedUsers(newValue); // newValue will be an array of user objects
  };

  const handleSectionChange = (event) => {
    const { target: { value } } = event;
    setSelectedSections(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleSubmit = async () => {
    const API_URL = process.env.REACT_APP_API_URL;
    
    // Base configuration for all toasts
    const baseToastConfig = {
      position: "bottom-left",
      autoClose: 5000, // 5 seconds
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      closeButton: false,
      icon: false,
      style: {
        color: "white",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "12px 16px",
        fontSize: "14px",
        whiteSpace: "nowrap",
        width: "auto",
        minWidth: "fit-content",
      },
    };

    if (modalType !== 'grant' && modalType !== 'revoke') {
      console.warn('Unknown modal type:', modalType);
      handleCloseModal();
      return;
    }

    const action = modalType === 'grant' ? 'grant' : 'revoke';
    
    // Handle different validation cases
    if (selectedUsers.length === 0 && selectedSections.length === 0) {
      toast.warn(`Please select at least one user and one section to ${action} access.`, {
        ...baseToastConfig,
        style: { 
          ...baseToastConfig.style, 
          backgroundColor: '#FFA500', 
          color: '#2E2E2E',
          fontWeight: 800
        }
      });
      return;
    }
    
    if (selectedUsers.length === 0) {
      toast.warn(`Please select at least one user to ${action} access.`, {
        ...baseToastConfig,
        style: { 
          ...baseToastConfig.style, 
          backgroundColor: '#FFA500', 
          color: '#2E2E2E',
          fontWeight: 800
        }
      });
      return;
    }
    
    if (selectedSections.length === 0) {
      toast.warn(`Please select at least one section to ${action} access.`, {
        ...baseToastConfig,
        style: { 
          ...baseToastConfig.style, 
          backgroundColor: '#FFA500', 
          color: '#2E2E2E',
          fontWeight: 800
        }
      });
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';
      const userIds = selectedUsers.map(user => user.id);
      const sectionTitles = selectedSections;
      
      const sectionIds = sectionTitles.map(title => 
        applicationSections.find(s => s.title === title)?.id
      ).filter(Boolean);

      if (sectionIds.length !== sectionTitles.length) {
        throw new Error('One or more sections could not be found');
      }

      const endpoint = modalType === 'grant' ? 'grant' : 'revoke';
      const response = await axios.post(`${API_URL}/api/user-access/${endpoint}`, {
        userIds,
        sectionIds
      });

      // Update local state
      if (selectedUsers.length === 1) {
        const userId = selectedUsers[0].id;
        const currentUserSections = [...(userSections[userId] || [])];
        
        if (modalType === 'grant') {
          const newSections = [...new Set([...currentUserSections, ...sectionIds])];
          setUserSections(prev => ({
            ...prev,
            [userId]: newSections
          }));
        } else {
          const updatedSections = currentUserSections.filter(
            id => !sectionIds.includes(id)
          );
          setUserSections(prev => ({
            ...prev,
            [userId]: updatedSections
          }));
        }
      }

      const sectionCount = sectionTitles.length;
      const sectionText = sectionCount === 1 ? 'section' : 'sections';
      toast.success(
        `Successfully ${modalType === 'grant' ? 'granted' : 'revoked'} access to ${sectionCount} ${sectionText}`,
        {
          ...baseToastConfig,
          style: { ...baseToastConfig.style, backgroundColor: '#28a745' }
        }
      );

      handleCloseModal();

    } catch (error) {
      console.error(`Error ${modalType}ing access:`, error);
      const errorMessage = error.response?.data?.message || 'Please try again.';
      toast.error(
        `Failed to ${modalType} access. ${errorMessage}`,
        {
          ...baseToastConfig,
          style: { ...baseToastConfig.style, backgroundColor: '#dc3545' }
        }
      );
    }
  };

  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <PageContainer>
      <BreadcrumbContainer>
        <BackButton onClick={handleBack}>
          <ArrowBackIosIcon sx={{ 
            fontSize: '10px', 
            marginRight: '1px', 
            marginLeft: '-23px',
            position: 'relative',
            top: '-1px'
          }} />
          Back
        </BackButton>
        <Breadcrumbs 
          aria-label="breadcrumb" 
          separator=">"
          sx={{ 
            marginLeft: 'auto',
            '& .MuiBreadcrumbs-separator': {
              color: 'var(--B5, #4299D4)',
              margin: '0 4px',
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&::before, &::after': {
                content: '" "',
                display: 'inline-block',
                width: '2px'
              }
            },
            '& .MuiBreadcrumbs-ol': {
              justifyContent: 'flex-end',
              '& a, & p': {
                color: 'var(--B5, #4299D4)',
                fontFamily: 'Proxima Nova',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textAlign: 'right',
                '&:hover': {
                  textDecoration: 'none',
                }
              },
              '& p': {
                color: 'var(--BK7, #6D6D6D)', // Slightly darker for current page
              }
            }
          }}
        >
          <Link underline="hover" href="/home">
            Dashboard
          </Link>
          <Typography>User Access Management</Typography>
        </Breadcrumbs>
      </BreadcrumbContainer>
      <TitleContainer>
        <PageTitle>User Access Management</PageTitle>
        <Divider sx={{ 
          mt: 3, 
          mb: 2, 
          width: '982px',
          height: '1px',
          backgroundColor: '#B1B1B1',
          border: 'none'
        }} />
        <PageDescription>
          <strong>Manage user access and permissions across the DevOps Buddy platform.</strong><br />
          Grant or revoke section-level access using email or GitHub usernames.
        </PageDescription>
      </TitleContainer>
      <CardsContainer sx={{ mt: 4 }}>
        <Grid container spacing={3}>
          <Grid item>
            <CardStyled onClick={() => handleOpenModal('grant')}>
              <IconWrapper>
                <img src={GrantAccessIcon} alt="Grant Access" />
              </IconWrapper>
              <Box sx={{ flex: 1 }}>
                <CardTitle>Grant Access</CardTitle>
                <CardDescription>
                  Assign users access to specific modules based on email or GitHub username. 
                  Enable secure, section-level permissions in just a few clicks.
                </CardDescription>
              </Box>
            </CardStyled>
          </Grid>
          <Grid item>
            <CardStyled onClick={() => handleOpenModal('revoke')}>
              <IconWrapper>
                <img src={RevokeAccessIcon} alt="Revoke Access" />
              </IconWrapper>
              <Box sx={{ flex: 1 }}>
                <CardTitle>Revoke Access</CardTitle>
                <CardDescription>
                  Remove access for users who no longer need permission to specific sections. 
                  Maintain platform security with easy access control management.
                </CardDescription>
              </Box>
            </CardStyled>
          </Grid>
        </Grid>
      </CardsContainer>

      <Modal
        open={openModal}
        onClose={(event, reason) => {
          // Only close if the close button was clicked (reason === 'closeButtonClick')
          // or if the escape key was pressed (reason === 'escapeKeyDown')
          if (reason === 'backdropClick') {
            return; // Prevent closing on backdrop click
          }
          handleCloseModal();
        }}
        aria-labelledby="access-management-modal-title"
        aria-describedby="access-management-modal-description"
        disableEscapeKeyDown={false}
      >
        <ModalStyle>
          <Box sx={{ mb: 3, position: 'relative' }}>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                right: 0,
                top: '-10px',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'transparent',
                }
              }}
            >
              <CloseIcon 
                sx={{
                  width: '18px',
                  height: '18px',
                  flexShrink: 0,
                  fill: 'var(--BK9,rgb(10, 10, 10))'
                }}
              />
            </IconButton>
            <ModalTitle id="access-management-modal-title" sx={{ mb: 1, pr: 3 }}>
              {modalType === 'grant' ? 'Grant Access' : 'Revoke Access'}
            </ModalTitle>
            <Divider sx={{ 
              width: '550px',
              height: '1px',
              backgroundColor: '#B1B1B1',
              border: 'none',
              mx: 'auto',
              display: 'block'
            }} />
            <Typography sx={{
              color: 'var(--BK7, #6D6D6D)',
              fontFamily: 'Proxima Nova',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
              mt: 2,
              mb: 3
            }}>
              {modalType === 'grant' 
                ? 'Assign users access to specific areas of the platform based on role or responsibility.'
                : 'Remove a user\'s access to selected sections of the platform securely and instantly.'
              }
            </Typography>
          </Box>
          
          <FormControl fullWidth margin="normal" variant="outlined" sx={{ 
            '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.75)',
            }
          }}>
            <Autocomplete
              id="users-select"
              options={allUsers}
              getOptionLabel={(option) => option.email || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedUsers.length > 0 ? selectedUsers[0] : null}
              onChange={(event, newValue) => {
                handleUserChange(event, newValue ? [newValue] : []);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedUsers.length === 0 ? 'Select User' : ''}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      color: selectedUsers.length === 0 ? '#2E2E2E' : 'inherit',
                      fontFamily: 'Proxima Nova',
                      fontSize: '14px',
                      fontWeight: 600,
                      '&::placeholder': {
                        color: '#2E2E2E',
                        opacity: 1,
                        fontWeight: 700,
                        letterSpacing: '0.3px'
                      }
                    },
                    ...(selectedUsers.length === 0 && {
                      '& input::placeholder': {
                        color: '#2E2E2E',
                        opacity: 1,
                        fontWeight: 700,
                        fontSize: '14px',
                        fontFamily: 'Proxima Nova',
                        letterSpacing: '0.3px'
                      }
                    })
                  }}
                />
              )}
              popupIcon={<DropdownIcon />}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.email || ''}
                </li>
              )}
              disableCloseOnSelect
            />
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined" sx={{ 
            '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.75)',
            }
          }}>
            <Select
              labelId="sections-select-label"
              id="sections-select"
              multiple
              displayEmpty
              disabled={isLoadingSections || selectedUsers.length !== 1}
              value={selectedSections}
              onChange={handleSectionChange}
              input={<OutlinedInput id="select-multiple-chip" />}
              IconComponent={DropdownIcon}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return (
                    <Typography 
                      variant="body1" 
                      color="textSecondary"
                      sx={{ 
                        color: '#9A9A9A',
                        fontSize: '14px',
                        fontFamily: 'Proxima Nova',
                        fontWeight: 400
                      }}
                    >
                      {isLoadingSections 
                        ? 'Loading sections...' 
                        : selectedUsers.length === 0 
                          ? 'Select a user first' 
                          : 'Select sections...'
                      }
                    </Typography>
                  );
                }
                return (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 0.5,
                      '&:hover': {
                        cursor: 'pointer'
                      }
                    }}
                  >
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={value}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        onDelete={(event) => {
                          event.stopPropagation();
                          setSelectedSections(prev => prev.filter(item => item !== value));
                        }}
                        sx={{
                          '& .MuiChip-deleteIcon': {
                            color: '#6D6D6D',
                            '&:hover': {
                              color: '#2E2E2E',
                              backgroundColor: 'transparent'
                            }
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      />
                    ))}
                  </Box>
                );
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 224,
                    width: 250,
                  },
                }
              }}
            >
              {getFilteredSections().length > 0 ? (
                getFilteredSections().map((section) => (
                  <MenuItem key={section.id} value={section.title}>
                    <Checkbox checked={selectedSections.includes(section.title)} />
                    <ListItemText primary={section.title} />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <ListItemText 
                    primary={
                      modalType === 'grant' 
                        ? 'User already has access to all sections' 
                        : 'User has no sections to revoke'
                    } 
                  />
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button 
              onClick={handleCloseModal} 
              sx={{
                display: 'inline-flex',
                padding: '8px 10px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                borderRadius: '4px',
                border: '1px solid var(--BK3, #D9D9D9)',
                color: 'var(--BK5, #9A9A9A)',
                fontFamily: 'Proxima Nova',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: 'normal',
                textTransform: 'uppercase',
                '&:hover': {
                  backgroundColor: 'transparent',
                  borderColor: 'var(--BK3, #D9D9D9)'
                }
              }}
            >
              CANCEL
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSubmit} 
              sx={{
                display: 'inline-flex',
                padding: '8px 10px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                borderRadius: '4px',
                backgroundColor: modalType === 'grant' ? '#2DB9A8' : '#DC1D1D',
                color: '#FFF',
                fontFamily: 'Proxima Nova',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: 'normal',
                textTransform: 'uppercase',
                '&:hover': {
                  backgroundColor: modalType === 'grant' ? '#2DB9A8' : '#DC1D1D',
                  boxShadow: 'none'
                },
                '&.MuiButton-contained': {
                  backgroundColor: modalType === 'grant' ? '#2DB9A8' : '#DC1D1D',
                  boxShadow: 'none'
                },
                '&.MuiButton-contained:hover': {
                  backgroundColor: modalType === 'grant' ? '#2DB9A8' : '#DC1D1D',
                  boxShadow: 'none'
                }
              }}
            >
              {modalType === 'grant' ? 'GRANT ACCESS' : 'REVOKE ACCESS'}
            </Button>
          </Box>
        </ModalStyle>
      </Modal>
    </PageContainer>
  );
}

export default UserConfigPage;
